import { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';

interface DateRangeFilterProps {
  value: {
    from: string;
    to: string;
  };
  onChange: (value: { from: string; to: string }) => void;
  label?: string;
}

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  compact?: boolean;
}

function DatePicker({ value, onChange, label, compact = false }: DatePickerProps) {
  const { C, T } = useTheme();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const parse = (s: string): Date => {
    if (!s) return new Date();
    const parts = s.split('-').map((n) => parseInt(n, 10));
    const y = parts[0] ?? new Date().getFullYear();
    const m = parts[1] ?? 1;
    const d = parts[2] ?? 1;
    return new Date(y, m - 1, d);
  };

  const iso = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const fmt = (s: string): string => {
    if (!s) return '—';
    const d = parse(s);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const [view, setView] = useState(() => {
    const d = parse(value);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  useEffect(() => {
    if (!open) return;
    const d = parse(value);
    setView({ y: d.getFullYear(), m: d.getMonth() });
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        popRef.current &&
        !popRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const reposition = () => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const popW = 278;
      const left = Math.min(Math.max(8, r.left), window.innerWidth - popW - 8);
      setCoords({ top: r.bottom + 6, left });
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  const dayNames = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
  const first = new Date(view.y, view.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Mon-first
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const daysInPrev = new Date(view.y, view.m, 0).getDate();
  const cells: { d: number; cur: boolean }[] = [];
  for (let i = startOffset - 1; i >= 0; i--) cells.push({ d: daysInPrev - i, cur: false });
  for (let i = 1; i <= daysInMonth; i++) cells.push({ d: i, cur: true });
  while (cells.length < 42) cells.push({ d: cells.length - startOffset - daysInMonth + 1, cur: false });

  const today = new Date();
  const todayIso = iso(today);
  const fontSize = compact ? 10 : 11;
  const padY = compact ? 5 : 6;

  const nav = (delta: number) => {
    setView((v) => {
      const m = v.m + delta;
      const nm = ((m % 12) + 12) % 12;
      const ny = v.y + Math.floor(m / 12);
      return { y: ny, m: nm };
    });
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <div
        onClick={() => {
          if (!open && ref.current) {
            const r = ref.current.getBoundingClientRect();
            const popW = 278;
            const left = Math.min(Math.max(8, r.left), window.innerWidth - popW - 8);
            const top = r.bottom + 6;
            setCoords({ top, left });
          }
          setOpen((o) => !o);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: `${padY}px 11px`,
          borderRadius: 999,
          border: `1px solid ${open ? C.blue : T.border}`,
          background: T.surfaceBg,
          cursor: 'pointer',
          transition: 'border-color .15s',
          boxShadow: open ? `0 0 0 3px ${C.blue}14` : 'none',
        }}
      >
        <svg
          width={12}
          height={12}
          viewBox="0 0 24 24"
          fill="none"
          stroke={open ? C.blue : T.textMuted}
          strokeWidth="2"
          strokeLinecap="round"
          style={{ cursor: 'pointer' }}
        >
          <rect x="3" y="4" width="18" height="18" rx="3" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span
          style={{
            fontSize: fontSize - 1,
            fontWeight: 600,
            color: T.textMuted,
            letterSpacing: '.02em',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize,
            fontWeight: 700,
            color: T.text,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fmt(value)}
        </span>
      </div>
      {open && (
        <div
          ref={popRef}
          className="mi-anim-fade-in"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            zIndex: 2000,
            background: T.surfaceBg,
            border: `1px solid ${T.border}`,
            borderRadius: 16,
            padding: 16,
            width: 278,
            boxShadow: T.shadow,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: T.clockText }}>
              {monthNames[view.m]} {view.y}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => nav(-1)}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  background: T.surfaceBg,
                  color: T.textBody,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width={11}
                  height={11}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                onClick={() => nav(1)}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  background: T.surfaceBg,
                  color: T.textBody,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width={11}
                  height={11}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 2,
              marginBottom: 4,
            }}
          >
            {dayNames.map((d, i) => (
              <div
                key={i}
                style={{
                  textAlign: 'center',
                  fontSize: 9,
                  fontWeight: 700,
                  color: T.textMuted,
                  padding: '4px 0',
                  letterSpacing: '.04em',
                }}
              >
                {d}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((c, i) => {
              const d = new Date(view.y, view.m + (c.cur ? 0 : i < startOffset ? -1 : 1), c.d);
              const dIso = iso(d);
              const selected = dIso === value;
              const isToday = dIso === todayIso;
              return (
                <button
                  key={i}
                  onClick={() => {
                    onChange(dIso);
                    setOpen(false);
                  }}
                  style={{
                    aspectRatio: '1',
                    borderRadius: 10,
                    border: 'none',
                    background: selected ? C.blue : isToday ? `${C.blue}14` : 'transparent',
                    color: selected ? '#fff' : c.cur ? T.text : T.textMuted,
                    opacity: c.cur ? 1 : 0.4,
                    fontSize: 10,
                    fontWeight: selected ? 700 : isToday ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'all .12s',
                    boxShadow: selected ? `0 3px 10px ${C.blue}40` : 'none',
                  }}
                  onMouseEnter={(ev) => {
                    if (!selected) ev.currentTarget.style.background = T.rowBg;
                  }}
                  onMouseLeave={(ev) => {
                    if (!selected)
                      ev.currentTarget.style.background = isToday ? `${C.blue}14` : 'transparent';
                  }}
                >
                  {c.d}
                </button>
              );
            })}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12,
              paddingTop: 10,
              borderTop: `1px solid ${T.border}`,
            }}
          >
            <button
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              style={{
                border: 'none',
                background: 'transparent',
                color: T.textMuted,
                fontFamily: 'inherit',
                fontSize: 10,
                fontWeight: 600,
                cursor: 'pointer',
                padding: '4px 2px',
              }}
            >
              Limpiar
            </button>
            <button
              onClick={() => {
                const t = iso(new Date());
                onChange(t);
                setOpen(false);
              }}
              style={{
                border: 'none',
                background: C.blue,
                color: '#fff',
                fontFamily: 'inherit',
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                padding: '6px 14px',
                borderRadius: 8,
              }}
            >
              Hoy
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function DateRangeFilter({ value, onChange, label }: DateRangeFilterProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
      {label && <div style={{ fontSize: 12, fontWeight: 700 }}>{label}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <DatePicker
          value={value.from}
          onChange={(from) => onChange({ from, to: value.to })}
          label="Desde"
          compact={false}
        />
        <DatePicker
          value={value.to}
          onChange={(to) => onChange({ from: value.from, to })}
          label="Hasta"
          compact={false}
        />
      </div>
    </div>
  );
}
